import { FormattedMessage } from 'react-intl';
import { observer } from 'mobx-react';
import React, { useEffect, useState } from 'react';
import Logo, { LOGO_TYPE } from '@product-framework/components/Logo';
import * as yup from 'yup';
import { Box, Button, Stack, TextField, Typography } from '@mui/material';
import {
  LOCAL_STORAGE_KEYCLOAK_CONFIG,
  LOCAL_STORAGE_OIDC_TOKEN_NAME,
} from '@constants';
import { GetDomain } from '@utils/index';
import Footer from '@product-framework/components/Footer';
import { UserManager, WebStorageStateStore } from 'oidc-client-ts';
import { useSearchParams } from 'react-router-dom';
import { clearAuthTokens, PageLoading, useStore } from '@ui-framework/index';
import FormErrorsArray from '../../../../ui-framework/components/AdvancedForms/FormErrorsArray';
import { DEFAULT_LOCALE } from '../../../../state/domain/Locale';

export const getKeyCloakConfigFromStorage = () => {
  try {
    const keyCloakConfig =
      JSON.parse(window.localStorage.getItem(LOCAL_STORAGE_KEYCLOAK_CONFIG)) ||
      null;
    const oidcTokenName =
      window.localStorage.getItem(LOCAL_STORAGE_OIDC_TOKEN_NAME) || null;
    return { keyCloakConfig, oidcTokenName };
  } catch {
    return { keyCloakConfig: null, oidcTokenName: null };
  }
};

export const checkIfKeyCloakConfigExists = () => {
  const { keyCloakConfig, oidcTokenName } = getKeyCloakConfigFromStorage();

  if (keyCloakConfig && oidcTokenName) {
    return true;
  }
  return false;
};

function KeycloakUserLoginForm() {
  const [loading, setLoading] = useState('');
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [apiErrors, setApiErrors] = useState([]);
  const [urlParams] = useSearchParams();
  const store = useStore();

  const redirectUrl = urlParams.get('redirect_url') || null;
  let decodedRedirectUrl = null;
  if (redirectUrl) {
    decodedRedirectUrl = decodeURI(redirectUrl);
  }

  const schema = yup.object().shape({
    email: yup
      .string()
      .email('Enter a valid email')
      .required('Email is required'),
  });

  const defaultKeycloakConfig = {
    realm: '',
    client_id: '',
    authority: '',
    redirect_uri: GetDomain(process.env.REACT_APP_API_HOST, '', false),
  };

  const getOidcStorageKey = (authority, clientId) => {
    // Strip trailing slash from authority URL if present
    const normalizedAuthority = authority.replace(/\/$/, '');
    return `oidc.user:${normalizedAuthority}:${clientId}`;
  };

  const getConfigByEmail = async ({ email: emailArg }) => {
    try {
      const apiUrl = `${GetDomain(
        process.env.REACT_APP_API_HOST,
        'api',
        true,
      )}/sxcl/v1/auth/config?email=${encodeURIComponent(emailArg)}`;
      const response = await fetch(apiUrl);
      const data = await response.json();
      if (response?.status >= 200 && response?.status < 300) {
        const locale = store.domain.locale.get() || DEFAULT_LOCALE;
        const params = {
          realm: data.realm,
          client_id: data.clientId,
          authority: data.authority,
          extraQueryParams: {
            ui_locales: locale,
            kc_locale: locale,
            login_hint: emailArg,
            ...(data.ssoAlias ? { kc_idp_hint: data.ssoAlias } : {}),
          },
        };
        return {
          data: params,
          errors: null,
        };
      }
      return {
        data: null,
        errors: data?.errors,
      };
    } catch (err) {
      return {
        data: null,
        errors: [{ message: err?.message }],
      };
    }
  };

  const setUpConfig = async ({ email: emailArg }) => {
    const responseConfigEmail = await getConfigByEmail({ email: emailArg });
    if (responseConfigEmail?.data) {
      const config = responseConfigEmail?.data;
      const keycloakParamFromApi = {
        ...defaultKeycloakConfig,
        ...config,
      };
      window.localStorage.setItem(
        LOCAL_STORAGE_KEYCLOAK_CONFIG,
        JSON.stringify(keycloakParamFromApi),
      );
      window.localStorage.setItem(
        LOCAL_STORAGE_OIDC_TOKEN_NAME,
        getOidcStorageKey(
          keycloakParamFromApi.authority,
          keycloakParamFromApi.client_id,
        ),
      );
      return true;
    }
    setApiErrors(responseConfigEmail?.errors || []);
    return false;
  };

  const processUserEmail = async emailArg => {
    setLoading(true);
    try {
      const isConfigSetup = await setUpConfig({ email: emailArg });
      if (isConfigSetup) {
        const { keyCloakConfig = null } = getKeyCloakConfigFromStorage();
        const userManager = new UserManager({
          ...keyCloakConfig,
          userStore: new WebStorageStateStore({ store: window.localStorage }),
        });
        // Storing redirect url in state so it can ne used in redirection once logged in
        userManager.signinRedirect({
          state: { returnPath: decodedRedirectUrl },
        });
      }
    } catch (err) {
      setApiErrors([{ message: err.message }]);
    }
    setLoading(false);
  };

  const handleSubmit = async e => {
    setError('');
    e.preventDefault();
    try {
      await schema.validate({ email });
      if (email.trim()) {
        processUserEmail(email.trim());
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const initialize = async () => {
    try {
      setLoading(true);
      clearAuthTokens(() => {}, store);
      await store.clearStoredData();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    initialize();
  }, []);

  if (loading) {
    return (
      <PageLoading>
        <FormattedMessage
          id="LOADING_AUTH_CONFIGURATION"
          defaultMessage="Loading auth configuration"
        />
        ...
      </PageLoading>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
      }}>
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: 'background.paper',
          paddingTop: '100px',
          marginLeft: { xs: 0, md: '50%' },
          width: { xs: '100%', md: '50%' },
        }}>
        <Box sx={{ margin: '0 auto', width: '80%' }}>
          <form noValidate onSubmit={handleSubmit}>
            <Stack spacing={2} direction="column">
              <Box sx={{ textAlign: { xs: 'center', md: 'left' } }}>
                <Logo size="large" type={LOGO_TYPE.FULL} />
              </Box>
              <Box>
                <TextField
                  inputProps={{
                    sx: {
                      height: '1em',
                    },
                  }}
                  fullWidth
                  id="email"
                  label={<FormattedMessage id="EMAIL" defaultMessage="Email" />}
                  type="email"
                  value={email}
                  onChange={e => {
                    setError(null);
                    setApiErrors([]);
                    setEmail(e.target.value);
                  }}
                  error={!!error}
                  helperText={error}
                />
              </Box>
              <FormErrorsArray
                withTitle={false}
                displayAsList={false}
                errors={apiErrors}
              />
              <Box>
                <Button fullWidth type="submit" variant="contained">
                  <FormattedMessage id="SUBMIT" defaultMessage="Submit" />
                </Button>
              </Box>
              <Box textAlign="center">
                <Typography variant="body2" color="text.secondary">
                  <FormattedMessage
                    id="BY_SIGNING_IN_YOU_AGREE_TO_OUR_TERMS_AND_ACCEPTABLE_USE_POLICY"
                    defaultMessage="By signing in, you agree to our Terms and Acceptable Use Policy"
                  />
                </Typography>
              </Box>
              {store.domain.locale.get() !== 'en' && (
                <Box textAlign="center">
                  <Button
                    onClick={() => {
                      store.domain.locale.set('en');
                    }}>
                    <Typography variant="body2">Switch to English?</Typography>
                  </Button>
                </Box>
              )}
            </Stack>
          </form>
        </Box>
      </Box>
      <Footer maxWidth="xl" />
    </Box>
  );
}

export default observer(KeycloakUserLoginForm);
